/*
 * Componentes visuais compartilhados pelas telas.
 * Sem biblioteca de UI: o painel usa Tailwind direto.
 *
 * Tudo o que muda a aparencia do sistema inteiro mora aqui. E o que permite
 * repaginar dezesseis telas sem abrir dezesseis arquivos — e, o que importa
 * mais, sem que uma delas fique para tras parecendo de outra epoca.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger' | 'subtle';
  size?: 'sm' | 'md';
}) {
  const variants = {
    /*
     * Gradiente curto e sombra colorida no botao principal: e o unico elemento
     * com relevo real na tela, entao ele diz sozinho onde a acao acontece.
     */
    primary:
      'bg-linear-to-b from-brand-500 to-brand-600 text-white shadow-sm shadow-brand-600/25 ' +
      'hover:from-brand-600 hover:to-brand-700 active:from-brand-700 active:to-brand-700 ' +
      'disabled:from-brand-300 disabled:to-brand-300 disabled:shadow-none',
    ghost:
      'bg-white text-slate-700 ring-1 ring-slate-200 ring-inset shadow-xs ' +
      'hover:bg-slate-50 hover:text-slate-900 hover:ring-slate-300 disabled:text-slate-400',
    subtle: 'bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:text-slate-400',
    danger:
      'bg-linear-to-b from-red-500 to-red-600 text-white shadow-sm shadow-red-600/25 ' +
      'hover:from-red-600 hover:to-red-700 disabled:from-red-300 disabled:to-red-300',
  } as const;

  const sizes = {
    sm: 'gap-1.5 rounded-lg px-2.5 py-1.5 text-xs',
    md: 'gap-2 rounded-xl px-4 py-2.5 text-sm',
  } as const;

  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center font-semibold transition-all duration-150 disabled:cursor-not-allowed ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      <input
        {...props}
        className={`w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-slate-900 ring-1 ring-slate-200 shadow-xs ring-inset transition-shadow placeholder:text-slate-400 hover:ring-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500 ${className}`}
      />
      {hint ? <span className="mt-1.5 block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

export function Alert({
  children,
  tone = 'error',
}: {
  children: ReactNode;
  tone?: 'error' | 'info' | 'warning' | 'success';
}) {
  /*
   * A faixa lateral colorida faz o tipo do aviso ser lido antes do texto — a
   * borda fina de antes exigia reparar na cor para distinguir erro de sucesso.
   */
  const tones = {
    error: 'bg-red-50 text-red-900 border-red-500',
    info: 'bg-brand-50 text-brand-950 border-brand-500',
    warning: 'bg-amber-50 text-amber-900 border-amber-500',
    success: 'bg-emerald-50 text-emerald-900 border-emerald-500',
  } as const;

  return (
    <div
      role="alert"
      className={`rounded-lg rounded-l border-l-3 px-3.5 py-2.5 text-sm ${tones[tone]}`}
    >
      {children}
    </div>
  );
}

export function Card({
  title,
  action,
  children,
  className = '',
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  /*
   * A classe `card` nao e decorativa: e por ela que a folha de estilo alcanca
   * as tabelas escritas a mao em cada tela. Removendo, as tabelas voltam a
   * aparencia crua.
   */
  return (
    <section
      className={`card rounded-2xl bg-white shadow-card ring-1 ring-slate-200/70 ${className}`}
    >
      {title ? (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {action}
        </header>
      ) : null}
      <div className="p-5">{children}</div>
    </section>
  );
}

/** Cabecalho de tela: titulo, explicacao e acoes, sempre no mesmo desenho. */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-sm text-slate-600">{description}</p>
        ) : null}
      </div>
      {action ? <div className="flex flex-wrap items-center gap-2">{action}</div> : null}
    </header>
  );
}

/** Etiqueta de estado. Um so desenho para status, contagem e classificacao. */
export function Badge({
  children,
  tone = 'neutral',
  dot = false,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'danger' | 'warning' | 'info';
  dot?: boolean;
}) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-700',
    success: 'bg-emerald-50 text-emerald-700',
    danger: 'bg-red-50 text-red-700',
    warning: 'bg-amber-50 text-amber-800',
    info: 'bg-brand-50 text-brand-800',
  } as const;

  const pontos = {
    neutral: 'bg-slate-400',
    success: 'bg-emerald-500',
    danger: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-brand-500',
  } as const;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {dot ? <span className={`size-1.5 shrink-0 rounded-full ${pontos[tone]}`} /> : null}
      {children}
    </span>
  );
}

/** Cartao de indicador do dashboard. */
export function StatCard({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card ring-1 ring-slate-200/70">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">{label}</p>
        {icon ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
            {icon}
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-900 tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

/** Estado vazio: diz o que falta, em vez de deixar a area em branco. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="py-10 text-center">
      <p className="text-sm font-medium text-slate-800">{title}</p>
      {description ? (
        <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">{description}</p>
      ) : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function Spinner({ label = 'Carregando...' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-slate-500">
      <span className="size-4 animate-spin rounded-full border-2 border-slate-200 border-t-brand-500" />
      {label}
    </div>
  );
}
